import React from 'react';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SEO, getBreadcrumbSchema } from '@/components/seo/SEO';
import { 
  FloralCorner, 
  FloralSeparator,
  PaperLayerBg 
} from '@/components/ui/FloralElements';
import { 
  Heart, 
  Leaf, 
  Users, 
  ShoppingBag,
  ExternalLink
} from 'lucide-react';

// Ranch photos
const ranchPhotos = {
  calves: 'https://customer-assets.emergentagent.com/job_0f922ffd-6fd3-4884-9c25-9862f533b31c/artifacts/9exx0j39_IMG_2561.JPEG',
  horses: 'https://customer-assets.emergentagent.com/job_0f922ffd-6fd3-4884-9c25-9862f533b31c/artifacts/m973uo9g_IMG_2599.jpg',
  cowCalf: 'https://customer-assets.emergentagent.com/job_0f922ffd-6fd3-4884-9c25-9862f533b31c/artifacts/uc00byr0_IMG_3232.JPEG',
  herd: 'https://customer-assets.emergentagent.com/job_0f922ffd-6fd3-4884-9c25-9862f533b31c/artifacts/enbbzva9_IMG_4574.jpeg',
  goatKid: 'https://customer-assets.emergentagent.com/job_0f922ffd-6fd3-4884-9c25-9862f533b31c/artifacts/ybcfo7j1_IMG_6810.jpeg',
  womanGoat: 'https://customer-assets.emergentagent.com/job_floral-upgrade-1/artifacts/n5mw12s5_IMG_6869.JPG',
  childGoat: 'https://customer-assets.emergentagent.com/job_floral-upgrade-1/artifacts/1ejb7upe_IMG_7758.JPG',
  aurora: 'https://customer-assets.emergentagent.com/job_floral-upgrade-1/artifacts/h2mpppe9_IMG_8669.jpeg',
  apron: 'https://customer-assets.emergentagent.com/job_floral-upgrade-1/artifacts/xuuv78u0_0%203.21.09%E2%80%AFPM.jpg',
};

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

export const GoatsPage = () => {
  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Goats & Ranch', path: '/goats' }
  ];

  return (
    <>
      <SEO
        title="Goats & Ranch Life"
        description="Meet our goats and explore life on our 7th generation Nebraska ranch. Learn about our ethically raised goats, sustainable cattle operation, and the making of our handmade goat milk products."
        path="/goats"
        structuredData={getBreadcrumbSchema(breadcrumbs)}
        keywords="goats, Nebraska ranch, goat milk products, ethically raised goats, cattle ranch, sustainable farming, ranch life, baby goats"
      />
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-gradient-hero overflow-hidden">
        <FloralCorner position="top-left" className="opacity-40" />
        <FloralCorner position="bottom-right" className="opacity-40" />
        
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={staggerContainer}
            >
              <motion.span 
                variants={fadeInUp}
                className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4"
              >
                <Heart className="h-4 w-4" />
                Meet Our Family
              </motion.span>
              <motion.h1 
                variants={fadeInUp}
                className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6"
              >
                Goats & Ranch Life
              </motion.h1>
              <motion.p 
                variants={fadeInUp}
                className="text-lg md:text-xl text-muted-foreground mb-6"
              >
                Meet the heart of our operation — our beloved goats and the Nebraska ranch we call home.
              </motion.p>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <PaperLayerBg>
                <img
                  src={ranchPhotos.womanGoat}
                  alt="Ranch owner cuddling a baby goat - showing the love and care we give our animals"
                  className="w-full h-[400px] object-cover rounded-xl"
                />
              </PaperLayerBg>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Our Family Ranch Section */}
      <section className="py-20 relative">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="max-w-4xl mx-auto"
          >
            <motion.h2 
              variants={fadeInUp}
              className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-8 text-center"
            >
              Our Family Ranch
            </motion.h2>
            
            <motion.div variants={fadeInUp} className="prose prose-lg text-muted-foreground space-y-4 mb-12">
              <p>
                We are a ranching family located in Northwest Nebraska raising the next generation to run the 
                family business. Our kids are the 7th generation on the family homestead, and are more help 
                than we can imagine.
              </p>
              <p>
                We pride ourselves in keeping all natural products that are good for you and good for the 
                environment. We ethically raise our animals (heavens sakes they have names) and we treat 
                them fantastically as they are our livelihood.
              </p>
              <p>
                What started as a way for a mom to stay home with her kids, quickly turned into a flourishing 
                business. We grow a plenty big garden, trying to keep up with demands of canned goods. We 
                source most our produce ourselves to keep the business more efficient and sustainable.
              </p>
              <p className="font-medium text-foreground">
                While this is a family business, we pride ourselves in being a business everyone wants to enjoy.
                <br />
                <span className="font-script text-xl text-primary">
                  Bring Little House on the Table into your house in a big way.
                </span>
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <FloralSeparator />

      {/* Values Cards */}
      <section className="py-20 bg-secondary relative">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8"
          >
            {[
              {
                icon: Heart,
                title: 'Ethically Raised',
                description: 'Our goats have names and personalities. We treat them as family because they are.'
              },
              {
                icon: Leaf,
                title: 'All Natural',
                description: 'No chemicals or shortcuts. Just natural products that are good for you and the earth.'
              },
              {
                icon: Users,
                title: '7th Generation',
                description: 'Our kids are learning the family business on the same homestead our ancestors settled.'
              }
            ].map((value, index) => (
              <motion.div key={index} variants={fadeInUp}>
                <Card className="raised-card h-full text-center p-8 border-0">
                  <CardContent className="p-0">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
                      <value.icon className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="font-serif text-xl font-semibold text-foreground mb-2">
                      {value.title}
                    </h3>
                    <p className="text-muted-foreground">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Ranch Photos Gallery */}
      <section className="py-20 relative overflow-hidden">
        <FloralCorner position="top-right" className="opacity-30" />
        
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.h2 
              variants={fadeInUp}
              className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-4"
            >
              Life on the Homestead
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-lg text-muted-foreground"
            >
              Moments from our daily life raising the next generation
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-2 lg:grid-cols-3 gap-4"
          >
            {[
              { src: ranchPhotos.childGoat, alt: 'Child bottle feeding a baby goat', span: 'lg:col-span-2' },
              { src: ranchPhotos.goatKid, alt: 'Child playing with goat', span: '' },
              { src: ranchPhotos.calves, alt: 'Young rancher with calves', span: '' },
              { src: ranchPhotos.horses, alt: 'Family horseback riding', span: '' },
              { src: ranchPhotos.herd, alt: 'Cattle herd with ranch dog', span: '' },
              { src: ranchPhotos.cowCalf, alt: 'Cow with newborn calf', span: '' },
            ].map((photo, index) => (
              <motion.div 
                key={index} 
                variants={fadeInUp}
                className={photo.span}
              >
                <div className="raised-card overflow-hidden rounded-xl h-full">
                  <img
                    src={photo.src}
                    alt={photo.alt}
                    className="w-full h-64 lg:h-80 object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <FloralSeparator />

      {/* Shop Goat Products */}
      <section className="py-20 bg-gradient-floral relative">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.span 
              variants={fadeInUp}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/10 text-accent text-sm font-medium mb-4"
            >
              <ShoppingBag className="h-4 w-4" />
              Shop Our Products
            </motion.span>
            <motion.h2 
              variants={fadeInUp}
              className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-4"
            >
              From Our Ranch to Your Home
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-lg text-muted-foreground max-w-2xl mx-auto"
            >
              From goat milk soaps to ranch-made goods, bring a piece of our Nebraska homestead into your home.
            </motion.p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="text-center flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button 
              asChild 
              size="lg"
              className="btn-beveled px-8 py-6 text-base font-semibold"
            >
              <a
                href="https://littlehouseonthetable.square.site/shop/goat-milk-soap/P4Y4YFP2KZWJFSPEXOI5AXLZ"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                <ShoppingBag className="h-5 w-5" />
                Shop Goat Milk Soap
                <ExternalLink className="h-4 w-4" />
              </a>
            </Button>
            <Button 
              asChild 
              size="lg"
              className="btn-beveled-sage px-8 py-6 text-base font-semibold"
            >
              <a
                href="https://littlehouseonthetable.square.site/s/shop"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2"
              >
                Browse All Products
                <ExternalLink className="h-5 w-5" />
              </a>
            </Button>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default GoatsPage;
